// src/lib/motion.ts
//
// Shared framer-motion presets. Variants and transition curves only — the
// CountUp animated-number primitive lives in `src/components/CountUp.tsx`
// because it needs JSX and motion.ts must stay .ts (lots of imports rely on
// the existing extension).
//
// Spring presets are split into three speeds so callers can match the
// intent of the moment:
//   - soft   (220/26): default list / row stagger, gentle settle
//   - snappy (360/30): page transitions, button confirmations
//   - big    (200/22): hero numbers, big-card entrances — slower, more
//                      "weighty" feel for editorial moments
//
// AnimatedRoutes in App.tsx uses snappy (stiffness 360, damping 30); these
// presets are calibrated to coexist with that without visual cross-talk.

import type { Transition, Variants } from 'framer-motion';

export const spring = {
  soft: { type: 'spring', stiffness: 220, damping: 26 } satisfies Transition,
  snappy: { type: 'spring', stiffness: 360, damping: 30 } satisfies Transition,
  big: { type: 'spring', stiffness: 200, damping: 22 } satisfies Transition,
};

export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const slideUp: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: spring.soft },
  exit: { opacity: 0, y: -4 },
};

export const staggerList: Variants = {
  animate: { transition: { staggerChildren: 0.04, delayChildren: 0.04 } },
};

export const staggerItem: Variants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: spring.soft },
};

/**
 * Editorial fade — slight y-translation + duration-based ease for hero
 * elements (HomeHero card, NetSavingsCard amount). Slower and more
 * intentional than slideUp; pairs with the Fraunces serif treatment.
 */
export const editorialFade: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
  exit: { opacity: 0, y: -4 },
};
